import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import { diffWords, diffSentences, Change } from 'diff';

interface DiffViewerProps {
  original: string;
  optimized: string;
  mode?: 'inline' | 'side-by-side' | 'changes-only';
}

export const DiffViewer: React.FC<DiffViewerProps> = ({
  original,
  optimized,
  mode = 'side-by-side'
}) => {
  const [viewMode, setViewMode] = React.useState(mode);
  const [showOriginal, setShowOriginal] = React.useState(false); // Default to hiding strikethrough
  const [showChanges, setShowChanges] = React.useState(true);

  // Strip HTML and exclude hidden SEO content for comparison
  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;

    // Remove hidden SEO sections that shouldn't be shown in diff
    // FAQ section (usually in a div with display:none)
    const faqSections = tmp.querySelectorAll('[style*="display:none"], [style*="display: none"]');
    faqSections.forEach(el => el.remove());

    // Remove any FAQ sections even if not hidden (they're schema markup)
    const faqHeaders = tmp.querySelectorAll('h2, h3');
    faqHeaders.forEach(header => {
      if (header.textContent &&
          (header.textContent.toLowerCase().includes('frequently asked') ||
           header.textContent.toLowerCase().includes('people also ask') ||
           header.textContent.toLowerCase().includes('faq'))) {
        // Remove the header and its following content
        let sibling = header.nextElementSibling;
        while (sibling && !['H1', 'H2', 'H3'].includes(sibling.tagName)) {
          const next = sibling.nextElementSibling;
          sibling.remove();
          sibling = next;
        }
        header.remove();
      }
    });

    // Remove About Eric sections
    const aboutSections = Array.from(tmp.querySelectorAll('*')).filter(el =>
      el.textContent && el.textContent.includes('About Eric')
    );
    aboutSections.forEach(el => el.parentElement?.remove());

    return tmp.textContent || tmp.innerText || '';
  };

  const originalText = useMemo(() => stripHtml(original), [original]);
  const optimizedText = useMemo(() => stripHtml(optimized), [optimized]);

  // Calculate diff
  const diff = useMemo(() => {
    return diffWords(originalText, optimizedText, { ignoreCase: false });
  }, [originalText, optimizedText]);

  // Calculate statistics
  const stats = useMemo(() => {
    let added = 0;
    let removed = 0;
    let modified = 0;

    diff.forEach((part: Change) => {
      if (part.added) {
        added += part.value.split(/\s+/).filter(w => w.length > 0).length;
      } else if (part.removed) {
        removed += part.value.split(/\s+/).filter(w => w.length > 0).length;
      }
    });

    // Count keyword occurrences
    const keywordRegex = /pizza in new york/gi;
    const originalKeywords = (originalText.match(keywordRegex) || []).length;
    const optimizedKeywords = (optimizedText.match(keywordRegex) || []).length;

    return {
      wordsAdded: added,
      wordsRemoved: removed,
      wordsModified: modified,
      keywordsAdded: optimizedKeywords - originalKeywords,
      totalChanges: diff.filter((p: Change) => p.added || p.removed).length
    };
  }, [diff, originalText, optimizedText]);

  const renderInlineDiff = () => {
    return (
      <Box sx={{
        p: 2,
        maxHeight: '600px',
        overflowY: 'auto',
        fontFamily: 'monospace',
        fontSize: '14px',
        lineHeight: 1.6,
        backgroundColor: '#f8f9fa'
      }}>
        {diff.map((part: Change, index: number) => {
          if (part.added) {
            return (
              <span
                key={index}
                style={{
                  backgroundColor: '#d4f4dd',
                  color: '#1e7e34',
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  margin: '0 2px'
                }}
              >
                {part.value}
              </span>
            );
          } else if (part.removed && showOriginal) {
            return (
              <span
                key={index}
                style={{
                  backgroundColor: '#fdd9d7',
                  color: '#a94442',
                  textDecoration: 'line-through',
                  opacity: 0.7,
                  padding: '2px 4px',
                  borderRadius: '3px',
                  margin: '0 2px'
                }}
              >
                {part.value}
              </span>
            );
          } else if (!part.added && !part.removed) {
            return (
              <span key={index} style={{ color: '#495057' }}>
                {part.value}
              </span>
            );
          }
          return null;
        })}
      </Box>
    );
  };

  const renderSideBySide = () => {
    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Paper sx={{ flex: 1, p: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="error">
            Original
          </Typography>
          <Box sx={{
            maxHeight: '500px',
            overflowY: 'auto',
            fontSize: '13px',
            lineHeight: 1.5
          }}>
            <div dangerouslySetInnerHTML={{ __html: original }} />
          </Box>
        </Paper>
        <Paper sx={{ flex: 1, p: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="success">
            Optimized
          </Typography>
          <Box sx={{
            maxHeight: '500px',
            overflowY: 'auto',
            fontSize: '13px',
            lineHeight: 1.5
          }}>
            <div dangerouslySetInnerHTML={{ __html: optimized }} />
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderChangesOnly = () => {
    const changes = diff.filter((part: Change) => part.added || part.removed);

    return (
      <Box sx={{ p: 2 }}>
        {changes.length === 0 ? (
          <Alert severity="info">No changes detected</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {changes.map((part: Change, index: number) => (
              <Paper
                key={index}
                sx={{
                  p: 1.5,
                  backgroundColor: part.added ? '#f0fdf4' : '#fef2f2',
                  borderLeft: '4px solid',
                  borderLeftColor: part.added ? '#22c55e' : '#ef4444'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Chip
                    label={part.added ? 'ADDED' : 'REMOVED'}
                    size="small"
                    color={part.added ? 'success' : 'error'}
                  />
                  <Typography variant="caption" color="textSecondary">
                    {part.value.split(/\s+/).filter(w => w.length > 0).length} words
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    color: part.added ? '#166534' : '#991b1b',
                    textDecoration: part.removed ? 'line-through' : 'none'
                  }}
                >
                  {part.value}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Controls */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="h6">Content Changes</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={`+${stats.wordsAdded} words`}
              size="small"
              color="success"
            />
            <Chip
              label={`-${stats.wordsRemoved} words`}
              size="small"
              color="error"
            />
            {stats.keywordsAdded > 0 && (
              <Chip
                label={`+${stats.keywordsAdded} keywords`}
                size="small"
                color="primary"
              />
            )}
          </Box>
        </Box>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="inline">
            <CompareIcon sx={{ mr: 0.5 }} fontSize="small" />
            Inline
          </ToggleButton>
          <ToggleButton value="side-by-side">
            Side by Side
          </ToggleButton>
          <ToggleButton value="changes-only">
            Changes Only
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Legend and Controls */}
      {viewMode === 'inline' && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: '#d4f4dd',
                  border: '1px solid #1e7e34',
                  borderRadius: 1
                }} />
                <Typography variant="caption">Added</Typography>
              </Box>
              {showOriginal && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{
                    width: 20,
                    height: 20,
                    backgroundColor: '#fdd9d7',
                    border: '1px solid #a94442',
                    borderRadius: 1,
                    textDecoration: 'line-through'
                  }} />
                  <Typography variant="caption">Removed</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: 1
                }} />
                <Typography variant="caption">Unchanged</Typography>
              </Box>
            </Box>

            <ToggleButton
              value="showOriginal"
              selected={showOriginal}
              onChange={() => setShowOriginal(!showOriginal)}
              size="small"
            >
              {showOriginal ? <HideIcon sx={{ mr: 0.5 }} fontSize="small" /> : <ViewIcon sx={{ mr: 0.5 }} fontSize="small" />}
              {showOriginal ? 'Hide' : 'Show'} Removed Text
            </ToggleButton>
          </Box>
        </Box>
      )}

      {/* Diff View */}
      <Paper sx={{ overflow: 'hidden' }}>
        {viewMode === 'inline' && renderInlineDiff()}
        {viewMode === 'side-by-side' && renderSideBySide()}
        {viewMode === 'changes-only' && renderChangesOnly()}
      </Paper>
    </Box>
  );
};